import React, { useState } from 'react';
import { usePrinterService } from '../../hooks/usePrinterService';
import PrinterSelectionModal from './PrinterSelectionModal';

const statusBadge = (connected) => {
  if (connected) {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
        Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
      Not Connected
    </span>
  );
};

export const PrinterQuickConnect = () => {
  const {
    state,
    disconnect,
    testPrint,
    clearQueue,
    restorePersistedConnection,
  } = usePrinterService();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleSelectPrinter = (device) => {
    setMessage(`Connected to ${device.name || 'Thermal Printer'}.`);
  };

  const handleReconnect = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await restorePersistedConnection({ autoReconnect: true });
      if (result.success && result.restored) {
        setMessage('Printer connection restored.');
      } else {
        setMessage(result.message || 'No saved printer to restore.');
      }
    } catch (error) {
      setMessage(error.message || 'Unable to restore connection.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await disconnect();
      setMessage('Printer disconnected.');
    } catch (error) {
      setMessage(error.message || 'Failed to disconnect printer.');
    } finally {
      setBusy(false);
    }
  };

  const handleTestPrint = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await testPrint({
        businessName: state.device?.name || state.persisted?.device?.name || 'TrackProfit',
      });
      setMessage('Test print sent to printer.');
    } catch (error) {
      setMessage(error.message || 'Failed to send test print.');
    } finally {
      setBusy(false);
    }
  };

  const pendingJobs = Array.isArray(state.queue) ? state.queue : [];

  return (
    <div className="bg-white border border-green-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Thermal Printer</h3>
          <p className="text-xs text-gray-500">
            Environment: {state.environment || 'web'} • Executor: {state.executorType}
          </p>
        </div>
        {statusBadge(state.connected)}
      </div>

      {state.device && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-gray-700 space-y-1">
          <div className="font-semibold text-green-700">{state.device.name || 'Thermal Printer'}</div>
          <div className="flex items-center gap-2 text-xs">
            <span>ID: <span className="font-mono">{state.device.id}</span></span>
            {state.device.transport && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                {state.device.transport === 'bluetooth' ? '📶 Bluetooth' : 
                 state.device.transport === 'usb' ? '🔌 USB' : 
                 state.device.transport}
              </span>
            )}
          </div>
        </div>
      )}

      {!state.connected && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={busy || !state.capabilities.canDiscover}
            className="px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Select Printer
          </button>
          <button
            type="button"
            onClick={handleReconnect}
            disabled={busy || !state.persisted}
            className="px-3 py-2 text-sm font-medium rounded-md border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Restore Saved Printer
          </button>
        </div>
      )}

      {state.connected && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleTestPrint}
            disabled={busy}
            className="px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Printing...' : 'Send Test Print'}
          </button>
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={busy}
            className="px-3 py-2 text-sm font-medium rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Disconnect
          </button>
        </div>
      )}

      {pendingJobs.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm space-y-2">
          <div className="font-semibold text-yellow-700">
            Pending print jobs ({pendingJobs.length})
          </div>
          <ul className="space-y-1">
            {pendingJobs.map((job) => (
              <li key={job.id} className="text-xs text-gray-600 flex justify-between">
                <span className="font-mono">{job.id.slice(-8)}</span>
                <span>{job.status || 'pending'}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={clearQueue}
            className="text-xs text-yellow-700 underline hover:text-yellow-800"
          >
            Clear queue
          </button>
        </div>
      )}

      {(message || state.lastError) && (
        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
          {message || state.lastError}
        </div>
      )}

      <PrinterSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleSelectPrinter}
      />
    </div>
  );
};

export default PrinterQuickConnect;



